import { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { InvoicesDashboard } from '../Dashboard/dashboard.types';
import { useDebtorAccount } from '@/app/shared/utils/hooks/useDebtorAccount';
import { InvoiceDetailNavigationCard } from './InvoiceDetailNavigationCard';
import { InvoiceDetailCard } from './InvoiceDetailCard';
import DocumentsCard from './DocumentsCard';
import { DetailType } from './DocumentsCard/DocumentsCard.types';
import { InvoiceContentContainer, InvoiceDetailsContentContainer } from './InvoiceDetail.StyledComponents';
import { RightColumn } from '@/app/shared/common.StyledComponents';
import { InvoiceNotesCard } from './InvoiceNotesCard';
import { PageRoutes } from '@/app/components/router/routes';
import { LocationState } from './invoiceDetail.types';
import LoadingPage from '@/app/shared/utils/LoadingPage';
import { FilterInfo, useSearchInvoices } from '@/app/shared/api/endpoints/SearchInvoices';
import { useAppContext } from '@/app/components/providers/AppContextProvider';

export const InvoiceDetail: React.FC = () => {
    const { invoiceId } = useParams();
    const { breakpoint } = useAppContext();
    const location = useLocation();
    const navigate = useNavigate();

    const {
        selectedDebtor,
        initialized: isDebtorInitialized,
        isDebtorSwitching,
    } = useDebtorAccount();

    const isInitialized = isDebtorInitialized && !!selectedDebtor;

    const [invoiceFound, setInvoiceFound] = useState(false);
    const [invoiceDetails, setInvoiceDetails] = useState<InvoicesDashboard>();

    const locationState = location.state as LocationState | null;
    const invoiceMap = locationState?.invoiceMap ?? [];

    const currentIndex = invoiceMap.findIndex(
        item => String(item.invoiceId) === invoiceId
    );

    const hasMultipleSelected = invoiceMap.length > 1;
    const hasPrevious = currentIndex > 0;
    const hasNext = currentIndex < invoiceMap.length - 1 && currentIndex !== -1;

    const handlePreviousInvoice = () => {
        if (hasPrevious) {
            const prevInvoice = invoiceMap[currentIndex - 1];
            navigate(
                PageRoutes.INVOICES.replace(':invoiceId', String(prevInvoice.invoiceId)),
                { state: locationState }
            );
        }
    };

    const handleNextInvoice = () => {
        if (hasNext) {
            const nextInvoice = invoiceMap[currentIndex + 1];
            navigate(
                PageRoutes.INVOICES.replace(':invoiceId', String(nextInvoice.invoiceId)),
                { state: locationState }
            );
        }
    };

    const filter: FilterInfo = {
        field: "assetId",
        filterType: "==",
        value: `${invoiceId}`
    };

    const searchRequest = {
        pageSize: 10,
        page: 1,
        isCsv: false,
        filter: filter,
        paymentsNotReceived: false,
        needsAttention: false,
    };

    const {
        data: invoiceData,
        isLoading,
    } = useSearchInvoices(searchRequest, {
        enabled: isInitialized,
    });

    useEffect(() => {
        if (invoiceData) {
            setInvoiceFound(true);
            setInvoiceDetails(invoiceData.results[0]);
        } else {
            setInvoiceFound(false);
        }
    }, [invoiceData]);

    if (isDebtorSwitching) {
        return <LoadingPage />;
    }

    if (!isInitialized || !invoiceFound) {
        if (isLoading) {
            return <LoadingPage />;
        }
        return null;
    }

    return (
        <InvoiceContentContainer>
            <InvoiceDetailsContentContainer>
                <InvoiceDetailNavigationCard
                    invoiceDetails={invoiceDetails}
                    hasMultipleSelected={hasMultipleSelected}
                    hasPrevious={hasPrevious}
                    hasNext={hasNext}
                    onPrevious={handlePreviousInvoice}
                    onNext={handleNextInvoice}
                    currentIndex={currentIndex}
                    totalSelected={invoiceMap.length}
                />
                <br />
                <InvoiceDetailCard invoiceDetails={invoiceDetails} />
                <br />
                <InvoiceNotesCard invoiceDetails={invoiceDetails} />
            </InvoiceDetailsContentContainer>
            <RightColumn breakpoint={breakpoint}>
                <DocumentsCard assetId={invoiceDetails?.invoiceId ?? 0} detailType={DetailType.Invoice} />
            </RightColumn>
        </InvoiceContentContainer>
    );
};